import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';

import {
  AuthLoginResponse,
  loginRequest,
  logoutRequest,
  ResetCodeData,
  resetCodeRequest,
  SetNewPasswordData,
  setNewPasswordRequest,
} from '../data/services/auth';
import {
  errorNotification,
  successNotification,
} from '../providers/mantine-notifications';
import { User, UserRole, UserType } from '../models/user';
import { LocalStorageKeys } from '../utils/enums';
import { rootNavigate } from '../components/CustomRouter';
import { translateServerHttpErrors } from '../utils/helpers';
import { Team } from '../models/team';
import { showTeamRequest } from '../data/services/teams';

interface InitialStateProps {
  loading: boolean;
  signed: boolean;
  user: User | null;
  token: string | null;
  userTeam?: Team;
}

const initialState: InitialStateProps = {
  signed: false,
  loading: false,
  user: null,
  token: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoggedUser: (state, action: PayloadAction<AuthLoginResponse>) => {
      state.user = action.payload.user;
      state.token = action.payload.access_token;
      state.signed = true;
      localStorage.setItem(
        LocalStorageKeys.AUTH_TOKEN,
        JSON.stringify(action.payload),
      );
    },
    initLoading: (state) => {
      state.loading = true;
    },
    endLoading: (state) => {
      state.loading = false;
    },
    handleLogout: (state) => {
      state.loading = initialState.loading;
      state.signed = initialState.signed;
      state.user = initialState.user;
      state.token = initialState.token;
      state.userTeam = undefined;
      localStorage.removeItem(LocalStorageKeys.AUTH_TOKEN);
    },
    setUserTeam: (state, action: PayloadAction<Team>) => {
      state.userTeam = action.payload;
    },
    updateUser: (state, action: PayloadAction<User>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
});

export const authActions = authSlice.actions;
const { endLoading, initLoading } = authActions;
export default authSlice.reducer;

export const loginDispatcher =
  (payload: { email: string; password: string }) =>
  async (dispatch: Dispatch<any>) => {
    try {
      dispatch(initLoading());
      const response = await loginRequest(payload);
      dispatch(endLoading());
      dispatch(authActions.setLoggedUser(response));
      if (
        response.user.type === UserType.USER &&
        response.user.role !== UserRole.COMPANY
      ) {
        dispatch(getUserTeamDispacher(Number(response.user.team)));
      }
      rootNavigate('/home');
    } catch (error) {
      dispatch(endLoading());
      errorNotification({
        title: 'Erro ao fazer login!',
        message: 'email ou senha incorreto 🔐',
      });
    }
  };

import { chatDb } from '../data/db';

export const logoutDispatcher = () => async (dispatch: Dispatch) => {
  await logoutRequest().catch(() => null);

  localStorage.removeItem(LocalStorageKeys.NOTIFICATIONS);
  localStorage.removeItem('redirect_url');

  chatDb.conversations.clear().catch(console.error);
  chatDb.messages.clear().catch(console.error);

  dispatch(authActions.handleLogout());
  rootNavigate('/');
};

export const requestResetCodeDispatcher =
  (payload: ResetCodeData) => async (dispatch: Dispatch) => {
    try {
      dispatch(initLoading());
      await resetCodeRequest(payload);
      dispatch(endLoading());
      successNotification({
        title: 'Alteração solicitada',
        message: 'verifique seu e-mail',
      });
    } catch (error) {
      dispatch(endLoading());
      errorNotification({
        title: 'Erro ao enviar código!',
        message: 'Verifique o e-mail informado e tente novamente.',
      });
    }
  };

export const newPasswordDispatcher =
  (code: string, payload: SetNewPasswordData) => async (dispatch: Dispatch) => {
    try {
      dispatch(initLoading());
      await setNewPasswordRequest(code, payload);
      dispatch(endLoading());
      successNotification({
        title: 'Senha alterada!',
        message: 'agora faça o login.',
      });
      rootNavigate('/');
    } catch (error: any) {
      dispatch(endLoading());
      errorNotification({
        title: 'Erro ao alterar senha!',
        message: translateServerHttpErrors(error, ''),
      });
    }
  };

export const getUserTeamDispacher =
  (teamId: number) => async (dispatch: Dispatch) => {
    try {
      dispatch(initLoading());
      const response = await showTeamRequest(teamId);
      dispatch(authActions.setUserTeam(response.data));
      dispatch(endLoading());
    } catch (error: any) {
      dispatch(endLoading());
      errorNotification({
        title: 'Erro ao buscar equipe de usuário!',
        message: translateServerHttpErrors(error, ''),
      });
    }
  };

export const resetLoadingDispatcher = () => async (dispatch: Dispatch) => {
  dispatch(endLoading());
};
